import { useSelector } from 'react-redux';
import { PermissionService } from '../PermissionService';
import { useMemo } from 'react';
import { accountPK_PermissionRoleSelector } from 'helpers/selectors/users';

/**
 * Custom React hook to check multiple permissions or get all permissions for the current user role.
 *
 * If an array of permission keys is provided, returns an array of booleans indicating which permissions
 * the current user has. If no array is provided, returns the full list of permissions available to the current role.
 *
 * @param {string[]|undefined} permissions - Optional array of permission keys to check (from {@link PERMISSIONS}).
 * @returns {boolean[]|string[]} - If `permissions` is provided: array of booleans (per each permission);
 *                                 if not provided: array of permission keys (strings) available for this role.
 *
 * @example
 * // Check multiple permissions
 * const [canEdit, canDelete] = usePermissions([PERMISSIONS.EDIT_USER, PERMISSIONS.DELETE_USER]);
 *
 * // Get all permissions for the current user
 * const allMyPermissions = usePermissions();
 */
export const usePermissions = (permissions) => {
    const PK_PermissionRole = useSelector(accountPK_PermissionRoleSelector);

    return useMemo(() => {
        if (permissions) {
            return PermissionService.hasPermissions(PK_PermissionRole, permissions);
        }

        return PermissionService.getPermissionsByRole(PK_PermissionRole) || [];
    }, [PK_PermissionRole, permissions]);
};
